import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import cambioButacaService from '@/services/perfil/cambioButaca';
import { toError } from '@/services/perfil/_normalize';

/**
 * useCambioButacaZonas — Estado y operaciones del paso de selección de zona
 * para el flujo de cambio de asiento (butaca o plaza).
 *
 * Responsabilidades:
 *  - Carga de zonas disponibles para el cambio de asiento del abonado.
 *  - Carga opcional del SVG del recinto (falla silenciosamente).
 *  - Selección / deselección de zona.
 *  - Guards: isZonasEmpty, puedeAvanzarPasoZona.
 *  - Defensa de contrato: emite `console.warn` en dev si `zona.tiene_plazas`
 *    está ausente con `tipoSvg === 2` (asume que la zona es de butacas).
 */
export function useCambioButacaZonas() {

    // ─── State ────────────────────────────────────────────────────────────
    const zonas = ref([]);
    const loadingZonas = ref(false);
    const errorZonas = ref(null);

    /** Zona elegida por el usuario. Incluye el objeto completo de la zona. */
    const zonaSeleccionada = ref(null);

    /** SVG del mapa del recinto. Se obtiene via cargarSvgRecinto(). */
    const svgRecinto = ref('');

    // ─── Computed ─────────────────────────────────────────────────────────

    /**
     * `true` si las zonas se cargaron sin error pero el array está vacío.
     */
    const isZonasEmpty = computed(
        () => !loadingZonas.value && !errorZonas.value && zonas.value.length === 0
    );

    /**
     * Paso Zona → puede avanzar si hay zona seleccionada.
     */
    const puedeAvanzarPasoZona = computed(
        () => Boolean(zonaSeleccionada.value)
    );

    // ─── API calls ────────────────────────────────────────────────────────

    /**
     * Carga el listado de zonas disponibles para el cambio de butaca.
     *
     * UNIFICA zonas normales (`/f/zonas`) y regiones anexas
     * (`/f/temporadas/{lista}/recintos/{recinto}/zonas/anexas`) en la misma
     * variable `zonas` que consume ZoneTableCard/VenueMapCard y el flujo
     * posterior (patrón legacy `SeleccionarRegion.vue`: normales primero,
     * anexas después). Sin esta unificación, un recinto que solo tiene
     * regiones anexas devuelve `[]` de `/f/zonas` → tabla de sectores vacía.
     *
     * @param {string|number} abonadoId
     * @param {string|number} [recintoId]  ID del recinto para filtrar zonas
     * @param {string|number} [listaAbonadoId]  ID de temporada para el endpoint de anexas ('' si no aplica)
     * @param {string}        [hashTeatro]  Hash del teatro para el param tid de anexas
     * @param {boolean}       [renovacion=false] Si true, el backend devuelve sólo precios de renovación
     */
    const cargarZonas = async(abonadoId, recintoId, listaAbonadoId = '', hashTeatro = null, renovacion = false) => {
        if (!abonadoId) return;

        loadingZonas.value = true;
        errorZonas.value = null;
        try {
            // Errores TOLERANTES: si un endpoint falla seguimos con el otro.
            // Solo si AMBOS fallan se propaga errorZonas. `primaryError` guarda
            // el primer error real para conservar el mensaje original.
            let primaryError = null;

            const fetchNormales = async() => {
                try {
                    const result = await cambioButacaService.getZonas(abonadoId, recintoId);
                    return { items: Array.isArray(result) ? result : [], ok: true };
                } catch (err) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('[useCambioButacaZonas] Error al obtener zonas normales:', err);
                    }
                    primaryError = primaryError ?? err;
                    return { items: [], ok: false };
                }
            };

            const fetchAnexas = async() => {
                if (!listaAbonadoId) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.warn('[useCambioButacaZonas] Sin listaAbonadoId, no se cargan zonas anexas.');
                    }
                    return { items: [], ok: false };
                }
                try {
                    const result = await cambioButacaService.getZonasAnexas(
                        abonadoId, recintoId, listaAbonadoId, hashTeatro, renovacion
                    );
                    return { items: Array.isArray(result) ? result : [], ok: true };
                } catch (err) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('[useCambioButacaZonas] Error al obtener zonas anexas:', err);
                    }
                    primaryError = primaryError ?? err;
                    return { items: [], ok: false };
                }
            };

            const [normales, anexas] = await Promise.all([fetchNormales(), fetchAnexas()]);

            // Ambas fallaron → error real (mensaje del primer endpoint).
            if (!normales.ok && !anexas.ok) {
                throw primaryError ?? new Error('zonas no disponibles');
            }

            // Unificar normales + anexas con dedupe defensivo por `id`
            // (normales primero, anexas después, como legacy).
            const unificadas = [];
            const ids = new Set();
            const merge = lista => {
                for (const z of lista) {
                    if (!z) continue;
                    if (z.id != null && ids.has(z.id)) continue;
                    if (z.id != null) ids.add(z.id);
                    unificadas.push(z);
                }
            };
            merge(normales.items);
            merge(anexas.items);

            // Defensa de contrato: si ninguna zona (unificada) expone `tiene_plazas`
            // (presente o ausente), avisar en dev. No bloqueamos el flujo.
            if (process.env.NODE_ENV !== 'production') {
                const hasPlazasFlag = unificadas.some(z => z.tiene_plazas === true || z.tiene_plazas === false);
                if (!hasPlazasFlag) {
                    console.warn(
                        '[useCambioButacaZonas] Ninguna zona expone el flag `tiene_plazas`. '
                        + 'La bifurcación Camino 1/Camino 2 dependerá exclusivamente del flag per-seat `es_plaza`.'
                    );
                }
            }

            // Filtrar zonas NO disponibles (esta_disponible / available).
            // El usuario solo debe ver zonas en las que realmente puede
            // seleccionar butaca. Se considera disponible si:
            //   - `available` es explícitamente `true` (normalizado por el
            //     servicio a partir de `esta_disponible`), o
            //   - `esta_disponible` es `true` (campo raw del backend).
            // Zonas sin flag se excluyen (defensa: si el backend no
            // explicita disponibilidad, mejor no mostrarlas).
            // El filtro `with_seats !== 0/false` (zonas sin butacas numeradas
            // NO elegibles) se aplica SOLO a zonas que NO tienen plazas
            // (`tiene_plazas` falsy): las zonas anexas con `tiene_plazas=1`
            // (plazas/parking) deben pasar aunque `with_seats` sea 0/false.
            const zonasFiltradas = unificadas.filter(
                z => (z.available === true || z.esta_disponible === true)
                    && (Boolean(z.tiene_plazas) || (z.with_seats !== 0 && z.with_seats !== false))
            );
            zonas.value = zonasFiltradas;
        } catch (err) {
            const normalized = toError(err);
            errorZonas.value = normalized.message ??
                t('perfil.cambio_asiento.stepper.error_zonas');
            zonas.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaZonas] Error al obtener zonas:', err);
            }
        } finally {
            loadingZonas.value = false;
        }
    };

    /**
     * Carga el SVG del mapa del recinto.
     * Falla silenciosamente: si no hay recinto_id o el endpoint falla, la UI sigue
     * funcionando solo con la tabla de zonas.
     *
     * @param {string|number} recintoId
     */
    const cargarSvgRecinto = async recintoId => {
        if (!recintoId) return;

        try {
            const svg = await cambioButacaService.getSvgRecinto(recintoId);
            svgRecinto.value = svg ?? '';
        } catch (err) {
            svgRecinto.value = '';
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaZonas] Error al obtener SVG del recinto:', err);
            }
        }
    };

    // ─── Selección ────────────────────────────────────────────────────────

    /**
     * Selecciona o deselecciona una zona.
     *
     * @param {Object} zona  Objeto zona del listado
     * @returns {boolean}  `true` si se seleccionó una zona nueva, `false` si se deseleccionó
     */
    const seleccionarZona = zona => {
        if (zonaSeleccionada.value && zonaSeleccionada.value.id === zona.id) {
            zonaSeleccionada.value = null;
            return false;
        }
        zonaSeleccionada.value = zona;
        return true;
    };

    /**
     * Limpia la zona seleccionada.
     */
    const limpiarZona = () => {
        zonaSeleccionada.value = null;
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        zonas.value = [];
        zonaSeleccionada.value = null;
        svgRecinto.value = '';
        errorZonas.value = null;
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        zonas,
        loadingZonas,
        errorZonas,
        zonaSeleccionada,
        svgRecinto,
        cargarZonas,
        cargarSvgRecinto,
        seleccionarZona,
        limpiarZona,
        isZonasEmpty,
        puedeAvanzarPasoZona,
        reset,
    };
}
